import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { publicationService } from '@/lib/api/services/publication'
import type { PaginationParams, PublicationFilterParams } from '@/lib/api/types'

// Query keys factory
export const publicationKeys = {
  all: ['publications'] as const,
  lists: () => [...publicationKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: PublicationFilterParams) =>
    [...publicationKeys.lists(), params, filters] as const,
  details: () => [...publicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...publicationKeys.details(), id] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...publicationKeys.all, 'search', searchTerm, params] as const,
  highlights: () => [...publicationKeys.all, 'highlights'] as const,
}

/**
 * Hook to fetch paginated publications with filters
 */
export const usePublications = (
  params: PaginationParams ,
  filters: PublicationFilterParams = {}
) => {
  return useQuery({
    queryKey: publicationKeys.list(params, filters),
    queryFn: () => publicationService.getPublications(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to search publications
 */
export const useSearchPublications = (
  searchTerm: string,
  params: PaginationParams 
) => {
  return useQuery({
    queryKey: publicationKeys.search(searchTerm, params),
    queryFn: () => publicationService.searchPublications(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to fetch a single publication by ID
 */
export const usePublication = (publicationId: string | undefined) => {
  return useQuery({
    queryKey: publicationKeys.detail(publicationId || ''),
    queryFn: () => publicationService.getPublicationById(publicationId!),
    enabled: !!publicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to increment view count for a publication
 */
export const useIncrementPublicationView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (publicationId: string) =>
      publicationService.incrementView(publicationId),
    onSuccess: (_, publicationId) => {
      // Invalidate the detail query to refetch with updated view count
      queryClient.invalidateQueries({ queryKey: publicationKeys.detail(publicationId) })
      // Also invalidate list queries to update view counts in lists
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
    },
  })
}

/**
 * Hook to increment download count for a publication
 */
export const useIncrementPublicationDownload = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (publicationId: string) =>
      publicationService.incrementDownload(publicationId),
    onSuccess: (_, publicationId) => {
      // Invalidate the detail query to refetch with updated download count
      queryClient.invalidateQueries({ queryKey: publicationKeys.detail(publicationId) })
      // Also invalidate list queries to update download counts in lists
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
    },
  })
}

/**
 * Hook to fetch home page highlights
 */
export const usePublicationHighlights = () => {
  return useQuery({
    queryKey: [...publicationKeys.all, 'highlights'],
    queryFn: () => publicationService.getHomePageHighlights(),
    staleTime: 5 * 60 * 1000,
  })
}

